import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import type Hls from "hls.js"
import { CrossOriginImage } from "./ui/CrossOriginImage"
import { isFirefox } from "@/utils/browser"
import { Icon } from "./icons/Icon"

declare module "react" {
	interface IframeHTMLAttributes<T> extends React.HTMLAttributes<T> {
		crossOrigin?: string
		credentialless?: string
	}
}

declare global {
	interface HTMLIFrameElement {
		credentialless?: boolean
	}
}

type VideoPlayerProps = {
	title: string
	vimeoUrl?: string
	youtubeId?: string
	hlsUrl?: string
	posterUrl?: string
	onPlay?: (target?: EventTarget) => void
}

let cachedCredentiallessSupport: boolean | null = null
const supportsCredentialless = () => {
	if (cachedCredentiallessSupport !== null) {
		return cachedCredentiallessSupport
	}

	if (typeof document === "undefined") {
		return false
	}

	const iframe = document.createElement("iframe")
	if ("credentialless" in iframe) {
		iframe.credentialless = true
		cachedCredentiallessSupport = iframe.credentialless === true
		return cachedCredentiallessSupport
	}

	const script = document.createElement("script")
	script.crossOrigin = "credentialless"
	cachedCredentiallessSupport = script.crossOrigin === "credentialless"
	return cachedCredentiallessSupport
}

let cachedNativeHlsSupport: boolean | null = null
const supportsNativeHls = () => {
	if (cachedNativeHlsSupport !== null) {
		return cachedNativeHlsSupport
	}

	if (typeof document === "undefined") {
		return false
	}

	const video = document.createElement("video")
	if (!video.canPlayType) {
		cachedNativeHlsSupport = false
		return cachedNativeHlsSupport
	}

	const canPlay =
		video.canPlayType("application/vnd.apple.mpegurl") ||
		video.canPlayType("application/x-mpegURL")

	cachedNativeHlsSupport = canPlay === "probably" || canPlay === "maybe"
	return cachedNativeHlsSupport
}

const getPlaybackMode = (vimeoUrl?: string, youtubeId?: string, hlsUrl?: string) => {
	if (vimeoUrl && supportsCredentialless()) {
		return "vimeo" as const
	}

	// If no HLS source is provided, always use YouTube when available so playback
	// remains functional on browsers without credentialless iframe support.
	if (youtubeId && (supportsCredentialless() || !hlsUrl)) {
		return "youtube" as const
	}

	return supportsNativeHls() ? "native" : "hlsjs"
}

const getYoutubePoster = (youtubeId: string) =>
	`https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`

const getIframeSrcWithAutoplay = (src: string) => {
	try {
		const url = new URL(src)
		url.searchParams.set("autoplay", "1")
		return url.toString()
	} catch {
		return src.includes("?") ? `${src}&autoplay=1` : `${src}?autoplay=1`
	}
}

function VideoPlayer({ title, vimeoUrl, youtubeId, hlsUrl, posterUrl, onPlay }: VideoPlayerProps) {
	const { t } = useTranslation("components")
	const playbackMode = getPlaybackMode(vimeoUrl, youtubeId, hlsUrl)
	const [isYoutubeStarted, setIsYoutubeStarted] = useState(false)
	const [isVimeoStarted, setIsVimeoStarted] = useState(false)

	if (playbackMode === "vimeo" && vimeoUrl) {
		const vimeoSrc = isVimeoStarted ? getIframeSrcWithAutoplay(vimeoUrl) : vimeoUrl

		return (
			<>
				<iframe
					width="100%"
					height="100%"
					frameBorder="0"
					crossOrigin="anonymous" // eslint-disable-line react/no-unknown-property
					style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
					src={vimeoSrc}
					title={title}
					allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
					referrerPolicy="strict-origin-when-cross-origin"
					allowFullScreen
					data-test="tutorial_vimeo"
					credentialless="" // eslint-disable-line react/no-unknown-property
				/>
				{!isVimeoStarted && (
					<button
						type="button"
						aria-label={title}
						className="absolute inset-0 z-10 cursor-pointer bg-transparent"
						data-test="tutorial_vimeo_play"
						onClick={(e) => {
							onPlay?.(e.target)
							setIsVimeoStarted(true)
						}}
					/>
				)}
			</>
		)
	}

	if (playbackMode === "youtube" && youtubeId) {
		const baseYoutubeSrc = `https://www.youtube.com/embed/${youtubeId}`
		const youtubeSrc = isYoutubeStarted ? `${baseYoutubeSrc}?autoplay=1` : baseYoutubeSrc

		return (
			<>
				<iframe
					width="100%"
					height="100%"
					crossOrigin="anonymous" // eslint-disable-line react/no-unknown-property
					style={{ position: "absolute", top: 0, left: 0 }}
					src={youtubeSrc}
					title={t("videoPlayer.youtubeVideoPlayer")} // i18n-prop-ignore: third-party iframe title
					allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
					allowFullScreen
					data-test="tutorial_youtube"
					credentialless="" // eslint-disable-line react/no-unknown-property
				/>
				{!isYoutubeStarted && (
					<button
						type="button"
						aria-label={title}
						className="absolute inset-0 z-10 cursor-pointer bg-transparent"
						data-test="tutorial_youtube_play"
						onClick={(e) => {
							onPlay?.(e.target)
							setIsYoutubeStarted(true)
						}}
					/>
				)}
			</>
		)
	}

	const useNative = playbackMode === "native"
	const fallbackPoster = posterUrl ?? (youtubeId ? getYoutubePoster(youtubeId) : undefined)

	return (
		<HlsVideoPlayer
			hlsUrl={hlsUrl}
			title={title}
			useNative={useNative}
			posterUrl={fallbackPoster}
			onPlay={onPlay}
		/>
	)
}

export default VideoPlayer

function HlsVideoPlayer({
	hlsUrl,
	title,
	useNative,
	posterUrl,
	onPlay,
}: {
	hlsUrl?: string
	title: string
	useNative: boolean
	posterUrl?: string
	onPlay?: (target?: EventTarget) => void
}) {
	const { t } = useTranslation("components")
	const videoRef = useRef<HTMLVideoElement>(null)
	const [hasStartedPlayback, setHasStartedPlayback] = useState(false)
	const [hasPosterError, setHasPosterError] = useState(false)
	const shouldUsePosterOverlay = isFirefox && Boolean(posterUrl)

	useEffect(() => {
		const videoElement = videoRef.current
		if (!videoElement || !hlsUrl) {
			return
		}

		videoElement.crossOrigin = "anonymous"

		let hlsInstance: Hls | null = null
		let active = true

		const cleanup = () => {
			active = false
			if (hlsInstance) {
				hlsInstance.destroy()
			}
			videoElement.removeAttribute("src")
			videoElement.load()
		}

		const setupNativePlayback = () => {
			videoElement.src = hlsUrl
		}

		const setupHlsJsPlayback = async () => {
			const { default: HlsConstructor } = await import("hls.js")
			if (!active || !HlsConstructor.isSupported()) {
				return
			}

			hlsInstance = new HlsConstructor({
				xhrSetup: (xhr) => {
					xhr.withCredentials = false
				},
			})
			hlsInstance.loadSource(hlsUrl)
			hlsInstance.attachMedia(videoElement)
		}

		if (useNative) {
			setupNativePlayback()
			return cleanup
		}

		void setupHlsJsPlayback()

		return cleanup
	}, [hlsUrl, useNative])

	return (
		<>
			<video
				ref={videoRef}
				crossOrigin="anonymous"
				controls
				playsInline
				preload="none"
				className="absolute left-0 top-0 size-full object-cover"
				aria-label={title}
				poster={shouldUsePosterOverlay ? undefined : posterUrl}
				data-test="tutorial_hls"
				onPlay={(e) => {
					setHasStartedPlayback(true)
					onPlay?.(e.target)
				}}
			>
				<track kind="captions" />
				<p>{t("videoPlayer.browserNotSupported")}</p>
			</video>
			{shouldUsePosterOverlay && !hasStartedPlayback && (
				<button
					type="button"
					aria-label={title}
					className="absolute inset-0 z-10 overflow-hidden rounded-[inherit] bg-base-800"
					onClick={() => {
						void videoRef.current?.play().catch(() => {})
					}}
				>
					{!hasPosterError && (
						<CrossOriginImage
							src={posterUrl}
							alt=""
							className="absolute inset-0 size-full object-cover"
							onError={() => setHasPosterError(true)}
						/>
					)}
					<div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
					<div className="absolute inset-0 flex items-center justify-center hover:opacity-60">
						<div className="flex size-12 items-center justify-center rounded-full bg-base-900/80 text-base-300 shadow-lg">
							<Icon name="play" size={34} />
						</div>
					</div>
				</button>
			)}
		</>
	)
}
